/* eslint-disable jsx-a11y/anchor-is-valid */
import {useState} from 'react'
import * as Yup from 'yup'
import clsx from 'clsx'
import {Link} from 'react-router-dom'
import {useFormik} from 'formik'
import * as Sentry from '@sentry/react'
import {login, sendLoginDetails} from '../core/_requests'
import {getConnectLoginLogoUrl} from '../../../../_metronic/helpers'
import {useAuth} from '../core/Auth'
import {useDeviceInfo} from './DeviceInfo'
import {pushNotificationService} from '../../../../services/pushNotificationApi'
import {clearConnectionTestSessionDismiss} from '../../../UI/connection-test/sessionDismiss'
import {MfaLoginFlow} from './MfaLoginFlow'
import {AuthModel} from '../core/_models'

const loginSchema = Yup.object().shape({
  email: Yup.string()
    .min(3, 'Minimum 3 symbols')
    .max(50, 'Maximum 50 symbols')
    .required('Email is required'),
  password: Yup.string().required('Password is required'),
})

const initialValues = {
  email: '',
  password: '',
}

export function Login() {
  const [loading, setLoading] = useState(false)
  const {saveAuth} = useAuth()
  const deviceInfo = useDeviceInfo()
  const [showPassword, setShowPassword] = useState(false)
  const [mfaFlow, setMfaFlow] = useState<{
    session: string
    requiresSetup: boolean
    methods?: string[]
    email: string
  } | null>(null)

  const togglePasswordVisibility = () => {
    setShowPassword((prevState) => !prevState)
  }

  const completeLogin = async (auth: AuthModel, email: string) => {
    const roles = Array.isArray(auth.roles)
      ? auth.roles
      : typeof auth.roles === 'string' && auth.roles
        ? [auth.roles]
        : []
    if (roles.some((role) => String(role).toLowerCase() === 'learner')) {
      setLoading(false)
      formik.setSubmitting(false)
      formik.setStatus('This account is for the Learning Platform only. Please sign in through Learning Studio.')
      saveAuth(undefined)
      sendLoginDetails(
        deviceInfo.deviceId,
        deviceInfo.deviceType,
        email,
        deviceInfo.ipAddress,
        deviceInfo.browser,
        deviceInfo.longitude,
        deviceInfo.latitude,
        deviceInfo.loginTimestamp,
        false
      ).catch(() => {})
      return
    }

    clearConnectionTestSessionDismiss()
    saveAuth(auth)

    try {
      await pushNotificationService.initializeAndRegister()
    } catch (e) {
      console.error('🔔 Login: Device token registration failed:', e)
    }

    sendLoginDetails(
      deviceInfo.deviceId,
      deviceInfo.deviceType,
      email,
      deviceInfo.ipAddress,
      deviceInfo.browser,
      deviceInfo.longitude,
      deviceInfo.latitude,
      deviceInfo.loginTimestamp,
      auth.isAuthenticated
    ).catch(() => {})

    setLoading(false)
    formik.setSubmitting(false)
  }

  const formik = useFormik({
    initialValues,
    validationSchema: loginSchema,
    onSubmit: async (values, {setStatus, setSubmitting}) => {
      setLoading(true)
      try {
        const {data: auth} = await login(values.email, values.password)
        const userID = auth?.email ?? values.email ?? 'unknown'
        const dateTime = new Date().toISOString()
        const firebaseDeviceToken = pushNotificationService.getCurrentDeviceToken()
        const loginPayload = {
          email: values.email,
          deviceId: deviceInfo.deviceId,
          deviceType: deviceInfo.deviceType,
          ipAddress: deviceInfo.ipAddress,
          browser: deviceInfo.browser,
          longitude: deviceInfo.longitude,
          latitude: deviceInfo.latitude,
          loginTimestamp: deviceInfo.loginTimestamp,
          isAuthenticated: auth.isAuthenticated,
          firebaseDeviceToken: firebaseDeviceToken ?? null,
        }
        Sentry.logger.info(`[${userID}] [${dateTime}] Login ${auth.isAuthenticated ? 'success' : 'failed'}`, {
          log_source: 'login',
          ...loginPayload,
        })

        if (auth.requiresMfaSetup || auth.requiresMfaChallenge) {
          setLoading(false)
          setSubmitting(false)
          setStatus(undefined)
          setMfaFlow({
            session: auth.mfaSession ?? '',
            requiresSetup: !!auth.requiresMfaSetup,
            methods: auth.availableMfaMethods,
            email: values.email,
          })
          return
        }

        if (auth.isAuthenticated === false) {
          setLoading(false)
          setSubmitting(false)
          setStatus(auth.message)
          sendLoginDetails(
            deviceInfo.deviceId,
            deviceInfo.deviceType,
            values.email,
            deviceInfo.ipAddress,
            deviceInfo.browser,
            deviceInfo.longitude,
            deviceInfo.latitude,
            deviceInfo.loginTimestamp,
            auth.isAuthenticated
          ).catch(() => {})
          return
        }

        await completeLogin(auth, values.email)
      } catch (error) {
        const userID = values.email || 'unknown'
        const dateTime = new Date().toISOString()
        const firebaseDeviceToken = pushNotificationService.getCurrentDeviceToken()
        Sentry.logger.error(`[${userID}] [${dateTime}] Login error`, {
          log_source: 'login',
          email: values.email,
          deviceId: deviceInfo.deviceId,
          deviceType: deviceInfo.deviceType,
          ipAddress: deviceInfo.ipAddress,
          browser: deviceInfo.browser,
          loginTimestamp: deviceInfo.loginTimestamp,
          firebaseDeviceToken: firebaseDeviceToken ?? null,
          error: error instanceof Error ? error.message : String(error),
        })
        console.error(error)
        saveAuth(undefined)
        setStatus('The login detail is incorrect')
        setSubmitting(false)
        setLoading(false)
      }
    },
  })

  if (mfaFlow) {
    return (
      <MfaLoginFlow
        mfaSession={mfaFlow.session}
        requiresSetup={mfaFlow.requiresSetup}
        availableMethods={mfaFlow.methods}
        onComplete={(auth) => {
          setMfaFlow(null)
          setLoading(true)
          completeLogin(auth, mfaFlow.email)
        }}
        onCancel={() => {
          setMfaFlow(null)
          saveAuth(undefined)
        }}
      />
    )
  }

  return (
    <form
      className='form w-100'
      onSubmit={formik.handleSubmit}
      noValidate
      id='kt_login_signin_form'
    >
      <div className='mb-11 mt-10'>
        <div className='d-flex align-items-end flex-warp'>
          <span
            className='text-dark fw-bold d-flex align-items-end'
            style={{fontSize: '28px', lineHeight: '30px'}}
          >
            Sign in to{' '}
          </span>
          <img
            alt='Connect logo'
            src={getConnectLoginLogoUrl()}
            className='ms-2'
            style={{height: '36px', width: 'auto', objectFit: 'contain'}}
          />
        </div>
      </div>

      {formik.status ? (
        <div className='mb-lg-15 alert alert-danger'>
          <div className='alert-text font-weight-bold'>{formik.status}</div>
        </div>
      ) : (
        <></>
      )}

      <div className='fv-row mb-8'>
        <label className='form-label fs-6 fw-semibold text-dark'>Email</label>
        <input
          placeholder='Email'
          {...formik.getFieldProps('email')}
          className={clsx(
            'form-control bg-transparent',
            {'is-invalid': formik.touched.email && formik.errors.email},
            {
              'is-valid': formik.touched.email && !formik.errors.email,
            }
          )}
          type='email'
          name='email'
          autoComplete='off'
        />
        {formik.touched.email && formik.errors.email && (
          <div className='fv-plugins-message-container'>
            <div className='fv-help-block'>
              <span role='alert'>{formik.errors.email}</span>
            </div>
          </div>
        )}
      </div>

      <div className='fv-row mb-3'>
        <label className='form-label fw-semibold text-dark fs-6 mb-0'>Password</label>
        <div className='position-relative'>
          <input
            placeholder='Password'
            type={showPassword ? 'text' : 'password'}
            autoComplete='off'
            {...formik.getFieldProps('password')}
            className={clsx(
              'form-control  bg-transparent pr-10',
              {
                'is-invalid': formik.touched.password && formik.errors.password,
              },
              {
                'is-valid': formik.touched.password && !formik.errors.password,
              }
            )}
            style={{paddingRight: '2.5rem'}}
          />

          <i
            className={clsx('bi', showPassword ? 'bi-eye-slash' : 'bi-eye', 'position-absolute', {
              'd-none': formik.values.password === '',
            })}
            onClick={togglePasswordVisibility}
            style={{
              right: '35px',
              top: '50%',
              transform: 'translateY(-50%)',
              cursor: 'pointer',
              fontSize: '1.25rem',
              color: '#555',
            }}
          ></i>
        </div>
        {formik.touched.password && formik.errors.password && (
          <div className='fv-plugins-message-container'>
            <div className='fv-help-block'>
              <span role='alert'>{formik.errors.password}</span>
            </div>
          </div>
        )}
      </div>

      <div className='d-flex flex-stack flex-wrap gap-3 fs-base fw-semibold mb-8'>
        <div />

        <Link to='/auth/forgot-password' className='link-dark'>
          Forgot Password ?
        </Link>
      </div>

      <div className='d-grid mb-10'>
        <button
          type='submit'
          id='kt_sign_in_submit'
          className='btn mt-3'
          style={{backgroundColor: '#D7383C', color: '#ffffff'}}
          disabled={formik.isSubmitting}
        >
          {!loading && <span className='indicator-label fw-bold fs-4'>Sign In</span>}
          {loading && (
            <span className='indicator-progress' style={{display: 'block'}}>
              Please wait...
              <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
            </span>
          )}
        </button>
      </div>
    </form>
  )
}
